import { z } from "zod";
import { AddressResponseSchema } from "./address";
import { OrderStatus } from "./order/order";
import { PaymentMethod, PaymentStatus } from "./payment";

export enum RouteStatus {
  NOT_STARTED = "Not_started",
  IN_PROGRESS = "In_progress",
  COMPLETED = "Completed",
}

export const ROUTE_UNSETTLED_STATUS = "Unsettled" as const;

const LocationSchema = z.object({
  latitude: z.string(),
  longitude: z.string(),
});

const RoutePointOrderPreviewSchema = z.object({
  id: z.string(),
  status: z.nativeEnum(OrderStatus),
  customer_name: z.string().nullable(),
  phone_number: z.string().nullable(),
  order_items: z.array(
    z.object({
      product_name: z.string(),
      quantity: z.number().int().nonnegative(),
    }),
  ),
  preffered_date: z.string().nullable(),
  preffered_time: z.string().nullable(),
  comment: z.string(),
  created_at: z.string(),
});

export const RoutePointSchema = z.object({
  id: z.number(),
  position: z.number().int().nonnegative(),
  order_id: z.string().nullable(),
  order_status: z.nativeEnum(OrderStatus).nullable(),
  phone_number: z.string().nullable(),
  payment_status: z.nativeEnum(PaymentStatus).nullable(),
  payment_method: z.nativeEnum(PaymentMethod).nullable(),
  load_kg: z.number().int().nonnegative(),
  order_preview: RoutePointOrderPreviewSchema.nullable(),
  timestamp: z.string().datetime().nullable(),
  skipped: z.boolean(),
  progress_completed_at: z.string().datetime().nullable(),
  address_note: z.string(),
  skipped_resolution: z
    .enum(["cancelled", "processing"])
    .nullable(),
  address: AddressResponseSchema,
  location: LocationSchema,
});

export type RoutePointResponse = z.infer<typeof RoutePointSchema>;

export const RouteResponseSchema = z.object({
  id: z.number(),
  driver_id: z.number(),
  driver_name: z.string(),
  route_date: z.string().nullable(),
  loaded_kg: z.number().int().nonnegative().nullable(),
  extra_sold_kg: z.number().int().nonnegative(),
  current_load_kg: z.number().int().nonnegative(),
  revenue_cents: z.number().int().nonnegative(),
  unresolved_skipped_count: z.number().int().nonnegative(),
  status: z.nativeEnum(RouteStatus),
  created_at: z.string(),
  route_points: z.array(RoutePointSchema),
});

export type RouteResponse = z.infer<typeof RouteResponseSchema>;

export const RouteLoadPayloadSchema = z.union([
  z.object({ loaded_kg: z.number().int().nonnegative() }).strict(),
  z.object({ extra_sold_kg: z.number().int().nonnegative() }).strict(),
]);

export type RouteLoadPayload = z.infer<typeof RouteLoadPayloadSchema>;

const RouteAssignedRoutePayloadSchema = z.object({
  driver_id: z.number(),
  order_ids: z.string().array().min(1),
  allow_capacity_override: z.boolean().optional(),
  excluded_order_override_ids: z.string().array().optional(),
});

const RoutePayloadSchema = z.object({
  route_date: z.string(),
  routes: z.array(RouteAssignedRoutePayloadSchema).min(1),
});

export type RoutePayload = z.infer<typeof RoutePayloadSchema>;

const RoutePreviewAssignedRoutePayloadSchema = z.object({
  driver_id: z.number(),
  order_ids: z.string().array(),
});

const RoutePreviewOptimizationRulesPayloadSchema = z.object({
  enforce_capacity: z.boolean().optional(),
  enforce_order_count: z.boolean().optional(),
  enforce_district_skills: z.boolean().optional(),
  enforce_assigned_driver: z.boolean().optional(),
  preserve_driver_assignments: z.boolean().optional(),
});

export const RoutePreviewPayloadSchema = z.object({
  route_date: z.string(),
  driver_ids: z.number().array(),
  order_ids: z.string().array().min(1).optional(),
  assigned_routes: z.array(RoutePreviewAssignedRoutePayloadSchema).optional(),
  optimization_rules: RoutePreviewOptimizationRulesPayloadSchema.optional(),
});
export type RoutePreviewPayload = z.infer<typeof RoutePreviewPayloadSchema>;

export const RoutePreviewOrderSchema = z.object({
  order_id: z.string(),
  person_name: z.string(),
  address_id: z.number(),
  address_label: z.string(),
  driver_note: z.string(),
  load_kg: z.number().int().nonnegative(),
  geolocation: LocationSchema.nullable(),
  preffered_date: z.string().nullable(),
  preffered_time: z.string().nullable(),
});
export type RoutePreviewOrder = z.infer<typeof RoutePreviewOrderSchema>;

export const RoutePreviewMissingGeolocationOrderSchema =
  RoutePreviewOrderSchema.extend({
    geolocation: z.null(),
  });
export type RoutePreviewMissingGeolocationOrder = z.infer<
  typeof RoutePreviewMissingGeolocationOrderSchema
>;

export enum RoutePreviewExcludedReason {
  WRONG_DATE = "wrong_date",
  MISSING_ADDRESS = "missing_address",
}

export const RoutePreviewExcludedOrderSchema = z.object({
  order_id: z.string(),
  person_name: z.string(),
  address_id: z.number().nullable(),
  address_label: z.string().nullable(),
  driver_note: z.string(),
  load_kg: z.number().int().nonnegative(),
  geolocation: LocationSchema.nullable(),
  preffered_date: z.string().nullable(),
  preffered_time: z.string().nullable(),
  reason: z.nativeEnum(RoutePreviewExcludedReason),
});
export type RoutePreviewExcludedOrder = z.infer<
  typeof RoutePreviewExcludedOrderSchema
>;

export const RoutePreviewProposedRouteSchema = z.object({
  driver_id: z.number(),
  driver_name: z.string(),
  capacity_kg: z.number().int().nonnegative(),
  max_order_count: z.number().int().positive().nullable(),
  total_load_kg: z.number().int().nonnegative(),
  remaining_capacity_kg: z.number().int().nonnegative(),
  order_count: z.number().int().nonnegative(),
  orders: z.array(RoutePreviewOrderSchema),
});
export type RoutePreviewProposedRoute = z.infer<
  typeof RoutePreviewProposedRouteSchema
>;

export enum RoutePreviewUnassignedReason {
  CAPACITY_OR_CONSTRAINTS = "capacity_or_constraints",
  ORDER_EXCEEDS_MAX_DRIVER_CAPACITY = "order_exceeds_max_driver_capacity",
  ASSIGNED_DRIVER_NOT_SELECTED = "assigned_driver_not_selected",
}

export const RoutePreviewUnassignedOrderSchema = RoutePreviewOrderSchema.extend({
  reason: z.nativeEnum(RoutePreviewUnassignedReason),
});
export type RoutePreviewUnassignedOrder = z.infer<
  typeof RoutePreviewUnassignedOrderSchema
>;

export enum RoutePreviewCapacityViolationCode {
  TOTAL_CAPACITY_EXCEEDED = "total_capacity_exceeded",
  ROUTE_CAPACITY_EXCEEDED = "route_capacity_exceeded",
  ORDER_EXCEEDS_MAX_DRIVER_CAPACITY = "order_exceeds_max_driver_capacity",
}

export const RoutePreviewCapacityViolationSchema = z.object({
  code: z.nativeEnum(RoutePreviewCapacityViolationCode),
  order_id: z.string().nullable(),
  driver_id: z.number().nullable(),
  driver_name: z.string().nullable(),
  load_kg: z.number().int().nonnegative(),
  capacity_kg: z.number().int().nonnegative(),
  overflow_kg: z.number().int().nonnegative(),
});
export type RoutePreviewCapacityViolation = z.infer<
  typeof RoutePreviewCapacityViolationSchema
>;

export const RoutePreviewResponseSchema = z.object({
  eligible_orders: z.array(RoutePreviewOrderSchema),
  missing_geolocation_orders: z.array(
    RoutePreviewMissingGeolocationOrderSchema,
  ),
  excluded_orders: z.array(RoutePreviewExcludedOrderSchema),
  proposed_routes: z.array(RoutePreviewProposedRouteSchema),
  unassigned_orders: z.array(RoutePreviewUnassignedOrderSchema),
  capacity_violations: z.array(RoutePreviewCapacityViolationSchema),
  can_continue: z.boolean(),
});
export type RoutePreviewResponse = z.infer<typeof RoutePreviewResponseSchema>;

export const RoutePreviewUnroutableErrorSchema = z.object({
  error: z.literal("route_engine_unroutable_orders"),
  detail: z.string(),
  order_ids: z.array(z.string()).min(1),
});
export type RoutePreviewUnroutableError = z.infer<
  typeof RoutePreviewUnroutableErrorSchema
>;

export const RoutePreviewDraftOptimizationRulesSchema = z.object({
  enforce_capacity: z.boolean(),
  enforce_order_count: z.boolean(),
  enforce_district_skills: z.boolean(),
  enforce_assigned_driver: z.boolean(),
  preserve_driver_assignments: z.boolean(),
});

export const RoutePreviewDraftSchema = z.object({
  route_date: z.string(),
  driver_ids: z.number().array(),
  selected_driver_ids: z.number().array(),
  preview: RoutePreviewResponseSchema,
  optimization_rules: RoutePreviewDraftOptimizationRulesSchema,
  excluded_order_override_ids: z.string().array(),
  geolocation_drafts: z.record(
    z.string(),
    z.object({
      latitude: z.string(),
      longitude: z.string(),
    }),
  ),
});
export type RoutePreviewDraft = z.infer<typeof RoutePreviewDraftSchema>;

export const RoutePreviewDraftResponseSchema = z.object({
  draft: RoutePreviewDraftSchema.nullable(),
});
export type RoutePreviewDraftResponse = z.infer<
  typeof RoutePreviewDraftResponseSchema
>;

export enum RouteSkippedResolutionAction {
  CANCELLED = "cancelled",
  PROCESSING = "processing",
}

const RoutePointPayloadSchema = z.object({
  timestamp: z.string().datetime().nullish(),
  skipped: z.boolean().nullish(),
  blik: z.boolean().optional(),
  address_note: z.string().max(2000).optional(),
});

export type RoutePointPayload = z.infer<typeof RoutePointPayloadSchema>;

export enum RouteProgressAction {
  CONTINUE = "continue",
  UNDO = "undo",
}

export const RouteProgressPayloadSchema = z.object({
  action: z.nativeEnum(RouteProgressAction),
  blik: z.boolean().optional(),
});

export type RouteProgressPayload = z.infer<typeof RouteProgressPayloadSchema>;

export const RouteReorderPayloadSchema = z.object({
  route_point_ids: z.array(z.number().int().positive()).min(1),
});

export type RouteReorderPayload = z.infer<typeof RouteReorderPayloadSchema>;

const RouteResolveSkippedItemPayloadSchema = z.object({
  route_point_id: z.number(),
  action: z.nativeEnum(RouteSkippedResolutionAction),
});

export const RouteResolveSkippedPayloadSchema = z.object({
  resolutions: z.array(RouteResolveSkippedItemPayloadSchema).min(1),
});

export type RouteResolveSkippedPayload = z.infer<
  typeof RouteResolveSkippedPayloadSchema
>;
